import { useState } from 'react';
import { Location } from '../../types/schema';
import { tGroupedDistrict } from '../../types';
import { Btn, BtnIconOnly, IconEdit, IconPlus, IconTrash } from '../ui';

interface DistrictCardProps {
    district: tGroupedDistrict;
    regionId: string;
    onEdit: () => void;
    onDelete: () => void;
    onEditLocation: (location: Location) => void;
    onDeleteLocation: (location: Location) => void;
    onAddLocation: (name: string) => void;
    getLocationEquipmentCount: (locationId: string) => number;
}

export const DistrictCard = ({
    district,
    regionId: _regionId,
    onEdit,
    onDelete,
    onEditLocation,
    onDeleteLocation,
    onAddLocation,
    getLocationEquipmentCount,
}: DistrictCardProps) => {
    const [newLocationName, setNewLocationName] = useState('');

    const handleAddLocation = () => {
        if (!newLocationName.trim()) return;
        onAddLocation(newLocationName.trim());
        setNewLocationName('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleAddLocation();
    };

    return (
        <div className="district-card">
            {/* District Header */}
            <div className="district-card-header">
                <span className="district-badge">DISTRICT</span>
                <div className="district-card-actions table-actions">
                    <BtnIconOnly
                        size="sm"
                        icon={<IconEdit />}
                        ariaLabel={`Edit ${district.name}`}
                        onClick={onEdit}
                    />
                    <BtnIconOnly
                        size="sm"
                        variant="danger"
                        icon={<IconTrash />}
                        ariaLabel={`Delete ${district.name}`}
                        onClick={onDelete}
                    />
                </div>
            </div>
            <h4 className="district-card-name">{district.name}</h4>
            {district.description && <p className="district-card-desc">{district.description}</p>}

            {/* Locations */}
            <div className="district-locations">
                <span className="district-locations-label">LOCATIONS · {district.locations.length}</span>
                {district.locations.length === 0 ? (
                    <p className="district-no-locations">No locations yet</p>
                ) : (
                    <ul className="district-location-list">
                        {district.locations.map((location, index) => (
                            <li key={location.id} className={`district-location-item ${index % 2 === 0 ? 'location-row-even' : 'location-row-odd'}`}>
                                <span className="location-dot">●</span>
                                <span className="location-name" title="Edit Location" onClick={() => onEditLocation(location)}>
                                    {location.name}
                                </span>
                                <span className="location-eq-count">
                                    {getLocationEquipmentCount(location.id)} eq
                                </span>
                                <BtnIconOnly
                                    size="sm"
                                    variant="danger"
                                    icon={<IconTrash />}
                                    ariaLabel={`Delete ${location.name}`}
                                    onClick={() => onDeleteLocation(location)}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Add Location Inline */}
            <div className="district-add-location">
                <input
                    type="text"
                    placeholder={district.locations.length === 0 ? "Add first location..." : "Add location..."}
                    value={newLocationName}
                    onChange={e => setNewLocationName(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <Btn
                    variant="outlined-faint"
                    buttonClasses="district-add-location-btn"
                    buttonIcon={<IconPlus />}
                    buttonText="Add"
                    buttonDisabled={!newLocationName.trim()}
                    buttonOnClick={handleAddLocation}
                    ariaLabel={`Add location to ${district.name}`}
                />
            </div>
        </div>
    );
};
